/**
 * ONE VERSION of a release, filling the right of the release page —
 * 10.2.0.27, selected in the rail beside it. The command center's
 * `VersionDetail`, read-only: the two disk-image tiles, the change log — which
 * IS this version's release notes, and since 2026-09-16 the only copy of them
 * — the documentation, and none of the curation — no Manage files, no
 * download journal, no Edit / Publish / Delete. A reseller takes what XOP
 * ships and changes nothing.
 */
import { Box, Chip, Typography } from "@mui/material";
import { BookOpen, FileText, HardDrive } from "lucide-react";
import type { JSX } from "react";

import { Markdown } from "../../knowledge-base/markdown.js";
import type { ReleaseRow } from "./api.js";
import { ReleaseArtifactTiles } from "./ReleaseArtifactTiles.js";
import { ReleaseDocuments } from "./ReleaseDocuments.js";
import { RELEASE_STATUS_COLOR, statusLabel } from "./release-status.js";

export function VersionDetail({ release }: { release: ReleaseRow }): JSX.Element {
  const notes = release.release_notes?.trim() ?? "";

  return (
    <Box
      component="article"
      data-testid={`version-detail-${release.version}`}
      aria-label={`Version ${release.version}`}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "background.paper",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          px: 2.5,
          py: 1.75,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography
              component="h4"
              sx={{
                fontFamily: (t) => t.typography.h6.fontFamily,
                fontSize: 20,
                fontWeight: 700,
                letterSpacing: "-0.01em",
                lineHeight: 1.2,
              }}
            >
              {release.version}
            </Typography>
            <Chip
              size="small"
              variant={release.status === "released" ? "filled" : "outlined"}
              color={RELEASE_STATUS_COLOR[release.status] ?? "default"}
              label={statusLabel(release.status)}
              sx={{ height: 22 }}
            />
          </Box>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.25 }}>
            {release.release_date ?? "No date yet"}
            {release.description ? ` · ${release.description}` : ""}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 2.5, py: 2.5, display: "grid", gap: 3 }}>
        <Box component="section" aria-label={`Downloads for ${release.version}`} sx={{ minWidth: 0 }}>
          <Typography sx={sectionHeadSx}>
            <HardDrive size={13} style={{ verticalAlign: "-2px", marginRight: 6 }} />
            Downloads
          </Typography>
          <ReleaseArtifactTiles release={release} />
        </Box>

        <Box
          sx={{
            display: "grid",
            gap: 3,
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 3fr) minmax(300px, 2fr)" },
          }}
        >
          <Box component="section" aria-label={`Change log for ${release.version}`} sx={{ minWidth: 0 }}>
            <Typography sx={sectionHeadSx}>
              <FileText size={13} style={{ verticalAlign: "-2px", marginRight: 6 }} />
              Change log
            </Typography>
            {notes ? (
              <Box sx={{ fontSize: 14, "& h1, & h2, & h3": { fontSize: 15, mt: 1.5, mb: 0.5 } }}>
                <Markdown source={notes} />
              </Box>
            ) : (
              <Typography sx={{ fontSize: 13, color: "text.disabled" }}>
                No change log written for this version yet.
              </Typography>
            )}
          </Box>

          <Box component="section" aria-label={`Documentation for ${release.version}`} sx={{ minWidth: 0 }}>
            <Typography sx={sectionHeadSx}>
              <BookOpen size={13} style={{ verticalAlign: "-2px", marginRight: 6 }} />
              Documentation
            </Typography>
            <ReleaseDocuments release={release} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const sectionHeadSx = {
  mb: 1,
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "text.disabled",
} as const;
